import React from "react";

const defaultBlockComponent = (blockType) =>
  ({
    unstyled: "p",
    paragraph: "p",
    "header-one": "h1",
    "header-two": "h2",
    "header-three": "h3",
    "header-four": "h4",
    "header-five": "h5",
    "header-six": "h6",
    "unordered-list-item": ["li", "ul"],
    "ordered-list-item": ["li", "ol"],
    blockquote: "blockquote",
    "code-block": "pre",
    atomic: "div",
  }[blockType] || "p");

const defaultStyleComponent = (style) =>
  ({
    BOLD: "b",
    ITALIC: "i",
    UNDERLINE: "u",
    STRIKETHROUGH: "s",
    CODE: "code",
  }[style] || "span");

const defaultEntityComponent = (entityType) =>
  ({
    IMAGE: ({ data }) => <img alt="" {...data} />,
    LINK: ({ data, children }) => <a href={data.url}>{children}</a>,
  }[entityType]);
const parseBlockComponent = (component) => {
  if (typeof component === "object" && component instanceof Array) {
    return component;
  }
  return [component, null];
};

const nbsp = " "; // WARNING: This is u00a0, not a space

const Renderer = ({
  data: { blocks, entityMap },
  getBlockComponent = defaultBlockComponent,
  getStyleComponent = defaultStyleComponent,
  getEntityComponent = defaultEntityComponent,
  textComponent = ({ children }) => children,
}) => {
  let blockList = [];
  let blocksDepthStack = [];

  // Deep clone blocks to avoid mutating the original data
  const clonedBlocks = blocks.map((block) => ({
    ...block,
    children: undefined, // Remove any existing children property
  }));

  for (let i = 0; i < clonedBlocks.length; i++) {
    const block = clonedBlocks[i];

    if (block.depth > 0) {
      // Safety check: ensure parent block exists at depth - 1
      // If not, treat this block as a root-level block
      if (blocksDepthStack[block.depth - 1]) {
        blocksDepthStack[block.depth - 1].children =
          blocksDepthStack[block.depth - 1].children || [];
        blocksDepthStack[block.depth - 1].children.push(block);
      } else {
        // No parent found, add to root level
        blockList.push(block);
      }
    } else {
      blockList.push(block);
    }
    blocksDepthStack[block.depth] = block;
    blocksDepthStack.splice(block.depth + 1);
  }

  // Track ordered list counters by depth level
  const olCounters = {};

  const renderArray = (array, depth = 0) => {
    let newArray = [];
    let innerArray = [];
    for (let i = 0; i < array.length; i++) {
      let block = array[i];
      if (innerArray.length > 0 && innerArray[0].type === block.type) {
        innerArray.push(block);
      } else {
        innerArray = [block];
        newArray.push(innerArray);
      }
    }
    return newArray.map((group, groupIndex) => {
      const blockType = group[0].type;
      const isOrderedList = blockType === "ordered-list-item";
      const wrapper = parseBlockComponent(getBlockComponent(blockType))[1];

      // Calculate start value for ordered lists
      let startValue = 1;
      if (isOrderedList) {
        if (olCounters[depth] === undefined) {
          olCounters[depth] = 0;
        }
        startValue = olCounters[depth] + 1;
        olCounters[depth] += group.length;
      } else {
        // Reset counter at this depth when we encounter a non-ordered-list block
        // This means a paragraph (Enter) will reset numbering, but nested lists won't
        const isUnorderedList = blockType === "unordered-list-item";
        if (!isUnorderedList) {
          // Non-list block (paragraph, header, etc.) - reset counter at this depth
          delete olCounters[depth];
        }
        // Reset deeper counters when we encounter any non-ordered-list
        for (let d = depth + 1; d <= 10; d++) {
          delete olCounters[d];
        }
      }

      const wrapperProps = { key: `${group[0].key}-${groupIndex}` };
      if (isOrderedList && wrapper === "ol") {
        wrapperProps.start = startValue;
      }

      return React.createElement(
        wrapper || React.Fragment,
        wrapperProps,
        group.map((block, blockIndex) =>
          React.createElement(
            parseBlockComponent(getBlockComponent(block.type))[0],
            { key: `${block.key}-${blockIndex}`, data: block.data },
            block.children
              ? [
                  renderRichText(block),
                  ...renderArray(block.children, depth + 1),
                ]
              : renderRichText(block)
          )
        )
      );
    });
  };

  const renderRichText = (block) => {
    let indexes = [0];

    block.entityRanges.forEach((entity) => {
      indexes.push(entity.offset);
      indexes.push(entity.offset + entity.length);
    });
    block.inlineStyleRanges.forEach((style) => {
      indexes.push(style.offset);
      indexes.push(style.offset + style.length);
    });
    indexes.sort((a, b) => a - b);

    indexes = indexes.filter(
      (v, i) => indexes.indexOf(v) === i && v !== block.text.length
    );

    let ranges = indexes
      .map((index, i) => ({
        start: index,
        end: indexes[i + 1] || block.text.length,
      }))
      .map(({ start, end }) => ({
        start,
        end,
        text: block.text.substring(start, end),
        styles: block.inlineStyleRanges
          .filter(
            (style) =>
              style.offset <= start && style.offset + style.length >= end
          )
          .map((style) => style.style),
        entities: block.entityRanges
          .filter(
            (entity) =>
              entity.offset <= start && entity.offset + entity.length >= end
          )
          .map((entity) => entityMap[entity.key]),
      }));

    const renderStyledText = (text, styles) => {
      let rendered = text;
      for (let i = 0; i < styles.length; i++) {
        rendered = React.createElement(
          getStyleComponent(styles[i]),
          {},
          rendered
        );
      }
      return rendered;
    };
    const renderEntities = (text, entities) => {
      let rendered = text;
      for (let i = 0; i < entities.length; i++) {
        rendered = React.createElement(
          getEntityComponent(entities[i].type),
          { data: entities[i].data },
          rendered
        );
      }
      return rendered;
    };
    if (ranges.length === 0) {
      return nbsp;
    }

    return ranges.map((range, index) =>
      React.cloneElement(
        renderEntities(
          renderStyledText(
            React.createElement(textComponent, {}, range.text),
            range.styles
          ),
          range.entities
        ),
        { key: index }
      )
    );
  };

  return renderArray(blockList);
};

export default Renderer;
